import { getWechatJsSdkConfig } from '@/api/wechat'

const WECHAT_SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
const DEFAULT_WECHAT_SHARE_IMAGE = '/images/vitaeai_logo.jpg'

let sdkLoadPromise
let sdkReadyPromise
let configuredUrl = ''

export function isWechatShareEnabled() {
  const value = String(import.meta.env.VITE_WECHAT_SHARE_ENABLED ?? 'true').trim().toLowerCase()
  return !['false', '0', 'no', 'off'].includes(value)
}

export function isWechatBrowser() {
  return typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
}

export function getWechatShareErrorMessage(error) {
  const message = String(error?.message || error || '')
  if (/未启用|未开启|disabled|not enabled/i.test(message)) {
    return '微信分享功能尚未启用，请联系管理员完成公众号配置'
  }
  if (/域名|domain|invalid url|白名单/i.test(message)) {
    return '当前网站域名未加入微信 JS 接口安全域名'
  }
  if (/签名|signature|config:/i.test(message)) {
    return '微信分享签名校验失败，请检查公众号域名配置'
  }
  if (/加载失败|未初始化|load/i.test(message)) {
    return '微信 JS-SDK 加载失败，请检查网络后重试'
  }
  if (/网络|超时|network|timeout/i.test(message)) {
    return '微信分享服务连接失败，请稍后重试'
  }
  return '微信分享配置失败，请稍后重试'
}

export function isWechatShareConfigurationUnavailable(error) {
  return /未启用|未开启|disabled|not enabled|app.?id|app.?secret/i.test(String(error?.message || error || ''))
}

export function getWechatShareUrl() {
  if (typeof window === 'undefined') return ''
  const url = new URL(window.location.href)
  url.hash = ''
  return url.toString()
}

export async function updateWechatAppMessageShareData({ title, desc, link, imgUrl }) {
  if (!isWechatBrowser()) return false

  const shareUrl = getWechatShareUrl()
  const wx = await ensureWechatReady(shareUrl)
  const data = {
    title: String(title || '共享文档'),
    desc: String(desc || 'VitaeAI 共享文档'),
    link: String(link || shareUrl),
    imgUrl: String(imgUrl || new URL(DEFAULT_WECHAT_SHARE_IMAGE, window.location.origin).href)
  }
  await invokeWechatShare(wx, data)
  // 兼容部分微信客户端：新接口负责更新分享卡片，旧接口负责注册菜单分享回调。
  if (typeof wx.onMenuShareAppMessage === 'function') {
    wx.onMenuShareAppMessage({ ...data, success: () => {}, cancel: () => {}, fail: () => {} })
  }
  return true
}

async function ensureWechatReady(url) {
  if (sdkReadyPromise && configuredUrl === url) return sdkReadyPromise

  configuredUrl = url
  sdkReadyPromise = (async () => {
    const result = await getWechatJsSdkConfig(url)
    const config = result?.data || result
    if (!config?.appId || !config?.timestamp || !config?.nonceStr || !config?.signature) {
      throw new Error('微信分享签名配置不完整')
    }

    const sdk = await loadWechatSdk()
    await new Promise((resolve, reject) => {
      sdk.config({
        debug: false,
        appId: config.appId,
        timestamp: Number(config.timestamp),
        nonceStr: config.nonceStr,
        signature: config.signature,
        jsApiList: ['updateAppMessageShareData', 'onMenuShareAppMessage']
      })
      sdk.ready(resolve)
      sdk.error(error => reject(new Error(error?.errMsg || '微信 JS-SDK 配置失败')))
    })
    return sdk
  })()

  try {
    return await sdkReadyPromise
  } catch (error) {
    sdkReadyPromise = undefined
    configuredUrl = ''
    throw error
  }
}

function loadWechatSdk() {
  if (window.wx) return Promise.resolve(window.wx)
  if (sdkLoadPromise) return sdkLoadPromise

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${WECHAT_SDK_URL}"]`)
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.wx), { once: true })
      existingScript.addEventListener('error', () => reject(new Error('微信 JS-SDK 加载失败')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = WECHAT_SDK_URL
    script.async = true
    script.onload = () => window.wx ? resolve(window.wx) : reject(new Error('微信 JS-SDK 未初始化'))
    script.onerror = () => reject(new Error('微信 JS-SDK 加载失败'))
    document.head.appendChild(script)
  })

  return sdkLoadPromise
}

function invokeWechatShare(wx, data) {
  if (typeof wx.updateAppMessageShareData !== 'function') {
    throw new Error('微信 JS-SDK 不支持好友分享配置')
  }
  return new Promise((resolve, reject) => {
    wx.updateAppMessageShareData({
      ...data,
      success: resolve,
      cancel: resolve,
      fail: error => reject(new Error(error?.errMsg || '微信好友分享配置失败'))
    })
  })
}
